import {
  Instagram,
  Facebook,
  Mail,
  Phone,
  MapPin,
  Star,
  ExternalLink,
} from "lucide-react";
import { BRAND, LOGO_URL, GMB_URL } from "../mock";
import { openExternal } from "../lib/openExternal";
import { buildBookingSmsHref } from "../lib/bookingSms";

export default function Footer() {
  return (
    <footer className="relative bg-[#08010f] border-t border-white/5 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <a href="/" className="flex items-center gap-3">
              <img
                src={LOGO_URL}
                alt="Jester Werks ceramic coating Mississauga"
                className="w-14 h-14 object-contain drop-shadow-[0_0_15px_rgba(141,57,238,0.5)]"
              />
              <div>
                <div className="font-display text-2xl tracking-wider">
                  JESTER WERKS
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-acid font-label mt-1">
                  Ceramic Coating Studio
                </div>
              </div>
            </a>
            <p className="mt-6 text-white/65 max-w-md leading-relaxed">
              Mississauga's most famous, most transparent and most reviewed
              ceramic coating studio. Hand-applied two-layer protection for years
              of glass-deep gloss and effortless cleaning.
            </p>

            <a
              href={GMB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/15 bg-white/5 hover:border-acid hover:bg-acid/10 transition-colors group"
             onClick={(e) => { e.preventDefault(); openExternal(GMB_URL); }}>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4"
                    style={{ color: "#FDC814", fill: "#FDC814" }}
                  />
                ))}
              </div>
              <span className="text-sm text-white font-label tracking-[0.15em]">
                {BRAND.rating.value} · {BRAND.rating.count}+ Google reviews
              </span>
              <ExternalLink className="w-3.5 h-3.5 text-white/40 group-hover:text-acid" />
            </a>

            <div className="mt-5 flex items-center gap-3">
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-jester-purple hover:border-jester transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={BRAND.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-jester-purple hover:border-jester transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="/media"
                aria-label="Videos & Media"
                className="px-4 h-10 rounded-full border border-white/10 flex items-center gap-2 hover:bg-acid hover:text-[#120127] hover:border-acid transition-colors text-xs font-label tracking-[0.2em]"
              >
                Videos &amp; Reels
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-acid mb-5 font-label">
              Two Studios
            </div>
            <ul className="space-y-3 text-white/70 text-sm">
              <li className="flex items-start gap-2">
                <Phone className="w-4 h-4 mt-0.5 text-acid" />
                <a href={`tel:${BRAND.phone}`} className="hover:text-acid">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 text-acid" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-acid">
                  {BRAND.email}
                </a>
              </li>
              {BRAND.locations.map((loc) => (
                <li key={loc.id} className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-acid flex-shrink-0" />
                  <span>
                    <span className="block text-acid text-[10px] uppercase tracking-[0.2em] font-label mb-0.5">
                      {loc.name}
                    </span>
                    {loc.full}
                  </span>
                </li>
              ))}
              <li className="text-white/50 pt-2 text-xs uppercase tracking-[0.2em] font-label">
                Open Daily · 2 PM to 12 AM
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-acid mb-5 font-label">
              Service Pages
            </div>
            <ul className="space-y-3 text-white/70 text-sm">
              {[
                { l: "Ceramic Coating Mississauga", h: "/ceramic-coating-mississauga" },
                { l: "Ceramic Coating Toronto", h: "/ceramic-coating-toronto" },
                { l: "Paint Correction", h: "/paint-correction-mississauga" },
                { l: "Auto Detailing", h: "/auto-detailing-mississauga" },
                { l: "Reviews", h: "/reviews" },
                { l: "Leave a Google Review", h: BRAND.socials.googleWriteReview, ext: true },
                { l: "Videos & Media", h: "/media" },
                { l: "Book Now via Text", h: buildBookingSmsHref() },
              ].map((l, i) => (
                <li key={i}>
                  <a
                    href={l.h}
                    {...(l.ext
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="hover:text-acid transition"
                  >
                    {l.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40 font-label tracking-[0.15em]">
            © {new Date().getFullYear()} Jester Werks. Crafted in Mississauga.
          </p>
          <p className="text-xs text-acid uppercase tracking-[0.25em] font-label">
            Locked in for 5 years.
          </p>
        </div>
      </div>
    </footer>
  );
}
